import { DataLayerEvents } from '@/datalayer/App.datalayer.constants';
import { useEffect } from 'react';

declare type DataLayerSubscriber = (dataLayer: any, unsubscribe?: () => void) => void;

export const ONE_TRUST_COOKIE_DEFAULT_CODE = 'C0001';
export const ONE_TRUST_COOKIE_ACCEPT_CODE = 'C0002';
export const ONE_TRUST_COOKIE_UPDATE = 'OneTrustGroupsUpdated';

/**
 * This hook will subscribe to the dataLayer events.
 *
 * @param eventName The name of the event to subscribe to.
 * @param subscriber The subscriber function.
 * @param options The options for the hook.
 */
export const useDataLayerEvent = (
  eventName: string,
  subscriber: DataLayerSubscriber,
  options?: { once?: boolean; fireOnMount?: boolean },
) => {
  /**
   * This effect will subscribe to the dataLayer events.
   */
  useEffect(() => {
    /** Initialize the dataLayer if it is not initialized. */
    window.dataLayer = window.dataLayer || [];

    /**
     * If the event should be fired on mount.
     */
    if (options?.fireOnMount && window.dataLayer?.findLast) {
      /**
       * Check if the event was already fired.
       */
      const existingDataLayerEvent = window.dataLayer?.findLast(
        (event: any) => event.event === eventName,
      );

      /**
       * If the event was already fired, call the subscriber.
       * This is useful when the subscriber is added after the event was fired.
       */
      if (existingDataLayerEvent) {
        subscriber(existingDataLayerEvent, () => {});

        /**
         * If the event was already fired and the subscriber should only be called once, return.
         */
        if (options?.once) return () => {};
      }
    }

    /**
     * This function will handle the dataLayer events.
     */
    const handler = (event: CustomEvent) => {
      /**
       * If the event is not the event we are looking for, return.
       */
      if (event.detail.event !== eventName) return;

      const unsubscribe = () =>
        window.removeEventListener(DataLayerEvents.DATALAYER_PUSH_EVENT, handler as EventListener);

      /**
       * Call the subscriber.
       */
      subscriber(event.detail, unsubscribe);

      /**
       * If the event should only be called once, remove the event listener.
       */
      if (options?.once) {
        unsubscribe();
      }
    };

    /**
     * Add the event listener.
     */
    window.addEventListener(DataLayerEvents.DATALAYER_PUSH_EVENT, handler as EventListener);

    /**
     * Return the unsubscribe function.
     */
    return () => {
      window.removeEventListener(DataLayerEvents.DATALAYER_PUSH_EVENT, handler as EventListener);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useDataLayerOneTrustConfirmationEvent = (
  subscriber: DataLayerSubscriber,
  options: { fireOnMount?: boolean; once?: boolean } = { fireOnMount: false, once: false },
) => {
  useDataLayerEvent(
    ONE_TRUST_COOKIE_UPDATE,
    (dataLayer, unsubscribe = () => {}) => {
      if (
        dataLayer?.OnetrustActiveGroups &&
        dataLayer?.OnetrustActiveGroups.includes(ONE_TRUST_COOKIE_ACCEPT_CODE)
      ) {
        subscriber(dataLayer, unsubscribe);
      }
    },
    options,
  );
};

export const useDataLayerOneTrustDefaultEvent = (
  subscriber: DataLayerSubscriber,
  options: { fireOnMount?: boolean; once?: boolean } = { fireOnMount: false, once: false },
) => {
  useDataLayerEvent(
    ONE_TRUST_COOKIE_UPDATE,
    (dataLayer, unsubscribe = () => {}) => {
      if (
        dataLayer?.OnetrustActiveGroups &&
        dataLayer?.OnetrustActiveGroups.includes(ONE_TRUST_COOKIE_DEFAULT_CODE)
      ) {
        subscriber(dataLayer, unsubscribe);
      }
    },
    options,
  );
};
